import React, { useMemo, useState } from "react";
import { IPersonalProfileProps } from "containers/SignUp/types";
import { EyeIcon, EyeOffIcon, LoadingSpinner } from "components/Common/Icons";
import { useCountryExtensionsQuery } from "store/apis/countryExtensionApi";
import { FormLabel, InputField, SelectField } from "components/FormFields";
import { components } from "react-select";
import Render from "components/Common/Render";

const TOS_URL = "https://www.bludot.io/terms-and-conditions";
const PRIVACY_POLICY_URL = "https://www.bludot.io/privacy-policy";

const PersonalProfile: React.FC<IPersonalProfileProps> = ({
  values,
  setFieldValue,
  errors,
  touched,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const { data: countryExtensions, isLoading: isLoadingExtensions } =
    useCountryExtensionsQuery();

  // Define option type
  type CountryOption = {
    value: string;
    label: string;
    flag?: string;
    code: string;
    name?: string;
  };

  // Format country extensions for react-select
  const countryOptions: CountryOption[] = useMemo(() => {
    if (!countryExtensions || countryExtensions.length === 0) {
      return [{ value: "+1", label: "US +1", flag: "", code: "US", name: "United States" }];
    }
    const options = countryExtensions.map((country) => ({
      value: country.dialCode,
      label: `${country.code} ${country.dialCode}`,
      flag: country.flag,
      code: country.code,
      name: country.name,
    }));
    // Sort to ensure US comes first
    return options.sort((a, b) => {
      if (a.code === "US") return -1;
      if (b.code === "US") return 1;
      return 0;
    });
  }, [countryExtensions]);

  // Get current selected country
  const selectedCountry = useMemo(() => {
    const found = countryOptions.find(
      (option) => option.value === values.countryCode,
    );
    // Default to US if not found
    const usOption = countryOptions.find((option) => option.code === "US");
    return found || usOption || countryOptions[0];
  }, [countryOptions, values.countryCode]);

  // Custom option component with flag emoji
  const CustomOption = (props: any) => {
    return (
      <components.Option {...props}>
        <div className="flex items-center gap-2">
          <Render when={!!props.data.flag}>
            <span className="text-xl" role="img" aria-label={props.data.name}>
              {props.data.flag}
            </span>
          </Render>
          <span>{props.data.label}</span>
        </div>
      </components.Option>
    );
  };

  // Custom single value component with flag emoji only (no text)
  const CustomSingleValue = (props: any) => {
    return (
      <components.SingleValue {...props}>
        <div className="flex items-center justify-center w-full h-full">
          <Render when={!!props.data.flag}>
            <span
              className="inline-flex items-center justify-center text-[28px] leading-none"
              role="img"
              aria-label={props.data.name}
              style={{ minWidth: "32px", width: "32px" }}
            >
              {props.data.flag}
            </span>
          </Render>
        </div>
      </components.SingleValue>
    );
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="w-full overflow-x-hidden space-y-6">
      <h2 className="text-[20px] font-poppins font-semibold text-[#4C4BC9] leading-[20px]">
        Personal Profile
      </h2>

      {/* Form Fields */}
      <div className="grid grid-cols-1 lg:grid-cols-2 w-full max-w-[1320px] gap-6">
        {/* Name - Order 1 on mobile, Left column on desktop */}
        <div className="lg:order-1 order-1">
          <FormLabel>Name</FormLabel>
          <InputField
            type="text"
            placeholder="Enter name"
            value={values.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFieldValue("name", e.target.value)
            }
            error={touched.name && errors.name ? errors.name : undefined}
            containerClass="mb-0"
            customClass="h-[56px] lg:h-[45px]"
          />
        </div>

        {/* Mobile Number - Order 2 on mobile, Left column row 2 on desktop */}
        <div className="lg:order-2 order-2">
          <FormLabel>Mobile Number</FormLabel>
          <div className="flex gap-2 w-full">
            <div className="w-[95px] md:w-[85px] flex-shrink-0 relative">
              <Render
                when={isLoadingExtensions}
                elseRender={
                  <SelectField<CountryOption>
                    value={selectedCountry}
                    options={countryOptions}
                    onChange={(selectedOption: any) => {
                      if (selectedOption) {
                        setFieldValue(
                          "countryCode",
                          selectedOption.value || "+1",
                        );
                        setFieldValue(
                          "countryName",
                          selectedOption.name || "United States",
                        );
                        setFieldValue(
                          "countryCodeShort",
                          selectedOption.code || "US",
                        );
                      }
                    }}
                    isSearchable
                    placeholder="🌍"
                    menuPlacement="auto"
                    menuPosition="fixed"
                    menuPortalTarget={document.body}
                    components={{
                      Option: CustomOption,
                      SingleValue: CustomSingleValue,
                      IndicatorSeparator: () => null,
                    }}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        minHeight: "56px",
                        height: "56px",
                        paddingLeft: "6px",
                        paddingRight: "6px",
                        borderRadius: "4px",
                        borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
                        boxShadow: state.isFocused
                          ? "0 0 0 1px #3b82f6"
                          : "none",
                        "&:hover": {
                          borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
                        },
                        "@media (min-width: 1024px)": {
                          minHeight: "45px",
                          height: "45px",
                        },
                      }),
                      dropdownIndicator: (base) => ({
                        ...base,
                        padding: "0 2px",
                      }),
                      singleValue: (base) => ({
                        ...base,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "28px",
                        lineHeight: "1",
                        margin: "0",
                      }),
                      valueContainer: (base) => ({
                        ...base,
                        padding: "0 2px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                        height: "100%",
                        overflow: "visible",
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                      menu: (base) => ({
                        ...base,
                        minWidth: "280px",
                        maxHeight: "400px",
                        borderRadius: "8px",
                        boxShadow:
                          "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                        border: "1px solid #d1d5db",
                        marginTop: "4px",
                        overflow: "hidden",
                      }),
                      menuList: (base) => ({
                        ...base,
                        maxHeight: "400px",
                        padding: "8px 0",
                        overflowY: "auto",
                        borderRadius: "8px",
                        "::-webkit-scrollbar": {
                          width: "8px",
                        },
                        "::-webkit-scrollbar-track": {
                          background: "#f1f1f1",
                          borderRadius: "4px",
                        },
                        "::-webkit-scrollbar-thumb": {
                          background: "#888",
                          borderRadius: "4px",
                        },
                        "::-webkit-scrollbar-thumb:hover": {
                          background: "#555",
                        },
                      }),
                      option: (base, state) => ({
                        ...base,
                        fontSize: "14px",
                        fontFamily: "Poppins",
                        padding: "10px 12px",
                        backgroundColor: state.isSelected
                          ? "#6B46C1"
                          : state.isFocused
                            ? "#f3f4f6"
                            : "white",
                        color: state.isSelected ? "white" : "#1f2937",
                        cursor: "pointer",
                        borderBottom: "1px solid #f3f4f6",
                        "&:last-child": {
                          borderBottom: "none",
                        },
                        "&:active": {
                          backgroundColor: "#6B46C1",
                        },
                      }),
                    }}
                  />
                }
              >
                <div className="w-full h-[56px] lg:h-[45px] border border-gray-300 flex items-center justify-center bg-white rounded">
                  <LoadingSpinner className="h-5 w-5 text-gray-500" />
                </div>
              </Render>
            </div>
            <div className="flex-1 min-w-0 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-700 font-medium font-poppins text-sm z-10">
                {values.countryCode}&nbsp;
              </span>
              <input
                type="tel"
                inputMode="numeric"
                placeholder="Enter number"
                value={values.phoneNumber}
                onChange={(e) => {
                  // Only allow digits
                  const sanitizedValue = e.target.value.replace(/\D/g, "");
                  setFieldValue("phoneNumber", sanitizedValue);
                }}
                className={`w-full h-[56px] lg:h-[45px] pr-3 lg:pr-3 border rounded focus:outline-none placeholder:text-[#BCBCBF] placeholder:font-normal placeholder:text-sm font-poppins text-base md:text-sm ${
                  touched.phoneNumber && errors.phoneNumber
                    ? "border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500"
                    : "border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                }`}
                style={{
                  paddingLeft: `${(values.countryCode?.length || 2) * 10 + 20}px`,
                }}
              />
            </div>
          </div>
          <Render when={!!(touched.phoneNumber && errors.phoneNumber)}>
            <div className="pt-1 pl-1 text-sm text-red-600">
              {errors.phoneNumber}
            </div>
          </Render>
        </div>

        {/* Email Address - Order 3 on mobile, Right column row 1 on desktop */}
        <div className="lg:order-3 order-3">
          <FormLabel>Email Address</FormLabel>
          <InputField
            type="email"
            placeholder="Enter email address"
            value={values.email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFieldValue("email", e.target.value)
            }
            error={touched.email && errors.email ? errors.email : undefined}
            containerClass="mb-0"
            customClass="h-[56px] lg:h-[45px]"
          />
        </div>

        {/* Password - Order 4 on mobile, Right column row 2 on desktop */}
        <div className="lg:order-4 order-4">
          <FormLabel>Password</FormLabel>
          <div className="relative w-full">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Enter password"
              value={values.password}
              onChange={(e) => setFieldValue("password", e.target.value)}
              className={`w-full h-[56px] lg:h-[45px] px-3 lg:p-3 pr-10 border rounded focus:outline-none placeholder:text-[#BCBCBF] placeholder:font-normal placeholder:text-sm font-poppins text-base md:text-sm ${
                touched.password && errors.password
                  ? "border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              }`}
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              <Render when={showPassword} elseRender={<EyeOffIcon />}>
                <EyeIcon />
              </Render>
            </button>
          </div>
          <Render when={!!(touched.password && errors.password)}>
            <div className="pt-1 pl-1 text-sm text-red-600">
              {errors.password}
            </div>
          </Render>
        </div>
      </div>

      {/* Terms and Conditions */}
      <div className="text-sm text-gray-700">
        By signing up you agree to our{" "}
        <a
          href={TOS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#2C3CFF] hover:opacity-80"
        >
          Terms of Service
        </a>{" "}
        and{" "}
        <a
          href={PRIVACY_POLICY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#2C3CFF] hover:opacity-80"
        >
          Privacy Policy
        </a>
      </div>
    </div>
  );
};

export default PersonalProfile;
